import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  Bell,
  BoxArrowRight,
  PersonCircle,
  ArrowLeft,
  CheckCircleFill,
  Clock,
  XCircleFill,
} from "react-bootstrap-icons";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import LOGO_URL from "../img/tmis_logo_orig.png";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_UPDATE_LEARN = `${API_BASE}/updateLearn`;
const ENDPOINT_UPLOAD = `${API_BASE}/uploadDocument`;
const ENDPOINT_SELECTS = `${API_BASE}/getLearnSelect`;
const ENDPOINT_ACTIVITY_BY_ID = `${API_BASE}/getActivityById`;
const ENDPOINT_LOGGEDIN = `${API_BASE}/getLoggedInDetails`;
const ENDPOINT_UPDATE_APPROVED_LEARN = `${API_BASE}/updateApprovedLearn`;

const NAV_BLUE = "#183B72";
const PANEL_BG = "#F1F3F6";
const PANEL_SURFACE = "#E7EAEE";
const BRAND = "#3D74F5";

const STATUS_STYLES = {
  Pending: {
    bg: "rgba(245,158,11,.14)",
    text: "#B45309",
    icon: <Clock size={14} />,
  },
  Approved: {
    bg: "rgba(16,185,129,.14)",
    text: "#047857",
    icon: <CheckCircleFill size={14} />,
  },
  Rejected: {
    bg: "rgba(239,68,68,.14)",
    text: "#B91C1C",
    icon: <XCircleFill size={14} />,
  },
  "Re-submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
  },
  "Re-Submit": {
    bg: "rgba(59,130,246,.14)",
    text: "#1D4ED8",
    icon: <Clock size={14} />,
  },
  Default: {
    bg: "rgba(148,163,184,.16)",
    text: "#475569",
    icon: <Clock size={14} />,
  },
};

function statusPillStyle(s) {
  const { bg, text } = STATUS_STYLES[s] || STATUS_STYLES.Default;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    background: bg,
    color: text,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}

function UploadCheckingModal({
  show,
  onHide,
  stage,
  pct,
  title,
  message,
  details,
}) {
  const isDone = stage === "done";
  const isChecking = stage === "checking";

  return (
    <Modal
      show={show}
      onHide={isDone ? onHide : undefined}
      centered
      backdrop="static"
      keyboard={isDone}
      className="custom-font"
    >
      <Modal.Body className="p-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="fw-bold" style={{ fontSize: 16 }}>
            {title}
          </div>
          {isDone && (
            <Button size="sm" variant="outline-secondary" onClick={onHide}>
              Close
            </Button>
          )}
        </div>

        <div
          className="text-muted mb-3"
          style={{ fontSize: 13, lineHeight: 1.5 }}
        >
          {message}
        </div>

        <ProgressBar now={pct} label={`${pct}%`} className="mb-2" />

        <div
          className="d-flex align-items-center justify-content-between mt-2"
          style={{ fontSize: 12 }}
        >
          <div className="text-muted">
            {isChecking
              ? "AI checking your documents…"
              : stage === "uploading"
                ? "Uploading…"
                : "Completed"}
          </div>
          <div className="text-muted">{details}</div>
        </div>

        {isChecking && (
          <div
            className="mt-3 d-flex align-items-center gap-2 text-muted"
            style={{ fontSize: 12 }}
          >
            <Spinner animation="border" size="sm" /> Please wait, this may take
            a moment…
          </div>
        )}

        {isDone && (
          <div
            className="mt-3 d-flex align-items-center gap-2"
            style={{ color: "#047857", fontWeight: 700 }}
          >
            <CheckCircleFill /> Completed
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function EditLearn() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const editingId = useMemo(() => {
    return (
      routeId ||
      location?.state?.id ||
      location?.state?.learnId ||
      query.get("id") ||
      ""
    );
  }, [routeId, location, query]);

  const ownerUserIdFromRoute = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return (
      location?.state?.userId ||
      location?.state?.ownerUserId ||
      qs.get("userId") ||
      ""
    );
  }, [location]);

  const [form, setForm] = useState({
    dateFrom: "",
    dateTo: "",
    activityName: "",
    activityTypeId: "",
    activityCategoryId: "",
    activityOther: "",
    activityProvider: "",
    isParticipated: false,
    trainerActaAclp: "",
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [proofOtherFiles, setProofOtherFiles] = useState([]);
  const [proofPpFiles, setProofPpFiles] = useState([]);
  const proofOtherRef = useRef(null);
  const proofPpRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [selects, setSelects] = useState({
    activityTypes: [],
    learnCategories: [],
  });
  const [loadingSelects, setLoadingSelects] = useState(true);
  const [selectsError, setSelectsError] = useState("");

  const [loadingDetail, setLoadingDetail] = useState(true);
  const [editRemarks, setEditRemarks] = useState("");
  const [existingDocs, setExistingDocs] = useState({
    cc: [],
    vl: [],
    sst: [],
    pp: [],
  });
  const [activityStatus, setActivityStatus] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalStage, setModalStage] = useState("uploading");
  const [modalPct, setModalPct] = useState(0);
  const [modalTitle, setModalTitle] = useState("Uploading Documents");
  const [modalMsg, setModalMsg] = useState(
    "Uploading your supporting documents. Please do not close this page.",
  );
  const [modalDetails, setModalDetails] = useState("");

  const FRONTEND_MIN_START = "2026-03-01";
  const [cycle, setCycle] = useState({
    start: FRONTEND_MIN_START,
    end: "",
    daysRemaining: null,
    eligible: true,
    learnReq: null,
    doReq: null,
  });
  const [loadingCycle, setLoadingCycle] = useState(true);

  const token = useMemo(
    () =>
      localStorage.getItem("authToken") || localStorage.getItem("token") || "",
    [],
  );

  const { displayName } = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return {
        displayName:
          localStorage.getItem("display_name") ||
          user.display_name ||
          localStorage.getItem("username") ||
          "User Acc",
      };
    } catch {
      return {
        displayName:
          localStorage.getItem("display_name") ||
          localStorage.getItem("username") ||
          "User Acc",
      };
    }
  }, []);

  const loggedInUserId = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        const id =
          u?.user_id ??
          u?.id ??
          u?.userId ??
          u?.data?.user_id ??
          u?.data?.id ??
          u?.data?.userId ??
          "";
        if (id) return String(id);
      }
      const loose =
        localStorage.getItem("user_id") ||
        localStorage.getItem("userid") ||
        localStorage.getItem("uid") ||
        "";
      return loose ? String(loose) : "";
    } catch {
      return "";
    }
  }, []);

  const targetUserId = useMemo(() => {
    return String(ownerUserIdFromRoute || loggedInUserId || "");
  }, [ownerUserIdFromRoute, loggedInUserId]);

  const findTypeById = (id) =>
    selects.activityTypes.find((x) => String(x.id) === String(id));

  const findCatById = (id) =>
    selects.learnCategories.find((x) => String(x.id) === String(id));

  const selectedType = findTypeById(form.activityTypeId);

  const isT2AG = useMemo(() => {
    const s = `${selectedType?.name || ""} ${selectedType?.description || ""}`
      .toLowerCase()
      .trim();
    return s.includes("t2ag");
  }, [selectedType]);

  const typeNameLC = useMemo(() => {
    return String(
      selectedType?.name || selectedType?.description || "",
    ).toLowerCase();
  }, [selectedType]);

  const isClassroomCourse = useMemo(() => {
    const s = (
      selectedType?.name ||
      selectedType?.description ||
      ""
    ).toLowerCase();
    return s.includes("classroom");
  }, [selectedType]);

  const showOtherUpload =
    !isT2AG && (!isClassroomCourse || form.trainerActaAclp === "no");

  const otherAttachmentType = useMemo(() => {
    if (isClassroomCourse) return "ccNoOfAttach";
    if (typeNameLC.includes("site")) return "sstAttached";
    if (typeNameLC.includes("virtual")) return "vlAttached";
    return "vlAttached";
  }, [isClassroomCourse, typeNameLC]);

  useEffect(() => {
    if (!isClassroomCourse) return;
    setForm((f) => {
      if (f.trainerActaAclp) return f;
      return { ...f, trainerActaAclp: "yes" };
    });
  }, [isClassroomCourse]);

  useEffect(() => {
    if (!showOtherUpload && proofOtherFiles.length) {
      setProofOtherFiles([]);
      if (proofOtherRef.current) proofOtherRef.current.value = "";
    }
  }, [showOtherUpload, proofOtherFiles.length]);

  const MB15 = 15 * 1024 * 1024;
  const allowed = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

  const addFiles = (prevList, fileList, maxCount = 3) => {
    const arr = Array.from(fileList || []);
    const valid = [];
    const skippedNames = [];
    const tooMany = [];

    const prevKey = (f) => `${f.name}:${f.size}`;
    const existing = new Set(prevList.map(prevKey));

    for (const f of arr) {
      if (f.size > MB15) {
        skippedNames.push(`${f.name} (too large)`);
        continue;
      }
      const k = `${f.name}:${f.size}`;
      if (existing.has(k)) {
        skippedNames.push(`${f.name} (duplicate)`);
        continue;
      }
      valid.push(f);
    }

    const spaceLeft = Math.max(0, maxCount - prevList.length);
    const toAdd = valid.slice(0, spaceLeft);
    const overflow = valid.slice(spaceLeft);
    overflow.forEach((f) =>
      tooMany.push(`${f.name} (exceeds limit of ${maxCount})`),
    );

    return {
      next: [...prevList, ...toAdd],
      skippedNames: [...skippedNames, ...tooMany],
    };
  };

  const toInputDate = (val) => {
    if (!val) return "";
    if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const [dd, mm, yyyy] = val.split("-");
      return `${yyyy}-${mm}-${dd}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  const formatDocDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/profile", { replace: true });
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setModalStage("uploading");
    setModalPct(0);
    setModalTitle("Uploading Documents");
    setModalMsg(
      "Uploading your supporting documents. Please do not close this page.",
    );
    setModalDetails("");
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingCycle(true);
        const { data } = await axios.get(ENDPOINT_LOGGEDIN, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const obj = data?.data || {};
        let cycleStart = obj?.cycleStart || FRONTEND_MIN_START;
        let cycleEnd = obj?.cycleEnd || "";
        if (cycleStart < FRONTEND_MIN_START) cycleStart = FRONTEND_MIN_START;

        setCycle({
          start: cycleStart,
          end: cycleEnd,
          daysRemaining: obj?.daysRemaining ?? null,
          eligible: obj?.eligible ?? true,
          learnReq: obj?.noOfLearnReq ?? obj?.learnReq ?? null,
          doReq: obj?.noOfDoReq ?? obj?.doReq ?? null,
        });
      } catch {
        setCycle((c) => ({ ...c, start: FRONTEND_MIN_START }));
      } finally {
        setLoadingCycle(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingSelects(true);
        setSelectsError("");

        const { data } = await axios.get(ENDPOINT_SELECTS, {
          params: { module: "learn" },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const rows = data?.data || data?.items || [];
        const isTypeOfActivity = (t) =>
          String(t || "").toLowerCase() === "typeofactivity";
        const isLearnCategory = (t) =>
          String(t || "").toLowerCase() === "learncategory";

        const activityTypes = rows
          .filter((r) => isTypeOfActivity(r.type))
          .map(({ id, name, description }) => ({ id, name, description }));

        const learnCategories = rows
          .filter((r) => isLearnCategory(r.type))
          .map(({ id, name, description }) => ({ id, name, description }));

        setSelects({ activityTypes, learnCategories });
      } catch (err) {
        setSelectsError(
          err?.response?.data?.message || "Unable to load selection lists.",
        );
      } finally {
        setLoadingSelects(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!editingId) {
      setLoadingDetail(false);
      setFormError("Missing LEARN activity id.");
      return;
    }

    (async () => {
      try {
        setLoadingDetail(true);
        setFormError("");

        const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module: "learn", id: editingId },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const rows = Array.isArray(data?.data) ? data.data : data?.data || [];
        const row = Array.isArray(rows) ? rows[0] : rows;
        const docs = Array.isArray(data?.docs) ? data.docs : [];

        if (!row) {
          setFormError("Unable to load activity details.");
          return;
        }

        let trainerAns = "";
        if (row.trainerActaAclp === "yes" || row.trainerActaAclp === "no") {
          trainerAns = row.trainerActaAclp;
        } else if (
          row.classroom_declaration === 1 ||
          row.classroom_declaration === "1" ||
          row.class_declare === 1 ||
          row.classDeclare === 1
        ) {
          trainerAns = "yes";
        } else if (
          row.classroom_declaration === 0 ||
          row.classroom_declaration === "0" ||
          row.class_declare === 0 ||
          row.classDeclare === 0
        ) {
          trainerAns = "no";
        }

        const remarks =
          row.remarks || row.remark || row.comments || row.comment || "";

        const grouped = { cc: [], vl: [], sst: [], pp: [] };
        docs.forEach((d) => {
          switch (d.attachment_type) {
            case "ccNoOfAttach":
              grouped.cc.push(d);
              break;
            case "vlAttached":
              grouped.vl.push(d);
              break;
            case "sstAttached":
              grouped.sst.push(d);
              break;
            case "ppAttached":
              grouped.pp.push(d);
              break;
            default:
              break;
          }
        });

        setForm({
          dateFrom:
            toInputDate(row.activity_date_from) || toInputDate(row.actDateFr),
          dateTo:
            toInputDate(row.activity_date_to) || toInputDate(row.actDateTo),
          activityName: row.activity_name || row.name || "",
          activityTypeId: row.activity_type ? String(row.activity_type) : "",
          activityCategoryId: row.activity_category
            ? String(row.activity_category)
            : "",
          activityOther:
            row.activity_category_others ||
            row.activityCategoryOthers ||
            row.category_others ||
            "",
          activityProvider:
            row.activity_provider ||
            row.provider ||
            row.activityProvider ||
            row.orgName ||
            "",
          isParticipated:
            Number(
              row.isParticipated ??
                row.is_participated ??
                row.participated ??
                1,
            ) === 1,
          trainerActaAclp: trainerAns || "",
        });

        setEditRemarks(remarks || "");
        setExistingDocs(grouped);
        setActivityStatus(row.status_name || row.status || "");
      } catch (err) {
        setFormError(
          err?.response?.data?.message || "Unable to load activity details.",
        );
      } finally {
        setLoadingDetail(false);
      }
    })();
  }, [editingId, token]);

  const validateForm = () => {
    if (!editingId) return "Missing LEARN activity id.";
    if (!targetUserId) return "Missing owner user id.";
    if (!token) return "Missing auth token. Please login again.";
    if (!form.dateFrom) return "Please select Activity Date From.";
    if (!form.dateTo) return "Please select Activity Date To.";

    const dFrom = new Date(form.dateFrom);
    const dTo = new Date(form.dateTo);
    if (dTo < dFrom) {
      return "Invalid date range: Activity Date From cannot be earlier than Activity Date To.";
    }

    const currentYear = new Date().getFullYear();
    const fromYear = dFrom.getFullYear();
    if (fromYear > currentYear) {
      return "You can only update LEARN activities for this year.";
    }

    if (!form.activityName.trim())
      return "Please provide the name of the activity.";
    if (!form.activityTypeId) return "Please select a type of activity.";
    if (!form.activityCategoryId) return "Please select a LEARN category.";

    const selectedCat = findCatById(form.activityCategoryId);
    if ((selectedCat?.name || "") === "Others" && !form.activityOther.trim()) {
      return "Please specify the other LEARN category.";
    }

    if (isClassroomCourse && !form.trainerActaAclp) {
      return "Please confirm whether the trainer has an ACTA/ACLP certification.";
    }

    if (!form.isParticipated) {
      return "You must confirm you participated.";
    }

    const minStart = cycle.start || FRONTEND_MIN_START;
    const maxEnd = cycle.end;
    if (form.dateFrom < minStart || form.dateTo < minStart) {
      return `Dates must be on/after ${minStart}.`;
    }
    if (maxEnd && (form.dateFrom > maxEnd || form.dateTo > maxEnd)) {
      return `Dates must be on/before ${maxEnd}.`;
    }

    return "";
  };

  const docIdOf = (d) => d?.doc_id ?? d?.id ?? d?.docId;

  const buildDeleteListForType = (attachmentType) => {
    const mapDocs = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((d) => ({ docId: docIdOf(d) }))
        .filter((x) => x.docId != null && x.docId !== "");

    switch (attachmentType) {
      case "ccNoOfAttach":
        return mapDocs(existingDocs.cc);
      case "vlAttached":
        return mapDocs(existingDocs.vl);
      case "sstAttached":
        return mapDocs(existingDocs.sst);
      case "ppAttached":
        return mapDocs(existingDocs.pp);
      default:
        return [];
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const isAiDoneFromDocs = (docs) => {
    const arr = Array.isArray(docs) ? docs : [];
    if (!arr.length) return false;

    return arr.every((d) => {
      const hasStatus =
        d?.documentStatus != null && String(d.documentStatus).trim() !== "";
      const hasValid = typeof d?.isValid !== "undefined" && d?.isValid !== null;
      const hasReadable =
        typeof d?.isReadable !== "undefined" && d?.isReadable !== null;
      return hasStatus || hasValid || hasReadable;
    });
  };

  const pollAiUntilDone = async (learnId, maxTries = 25, waitMs = 2000) => {
    for (let i = 0; i < maxTries; i++) {
      try {
        const { data } = await axios.get(ENDPOINT_ACTIVITY_BY_ID, {
          params: { module: "learn", id: learnId },
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          timeout: 20000,
        });

        const docs = Array.isArray(data?.docs) ? data.docs : [];
        if (isAiDoneFromDocs(docs)) return true;
      } catch {
        // keep polling
      }

      await sleep(waitMs);
      setModalDetails(`Checking… (${i + 1}/${maxTries})`);
      setModalPct(100);
    }
    return false;
  };

  async function uploadBucketsSequentially({
    learnId,
    otherFiles = [],
    ppFiles = [],
  }) {
    const jobs = [];
    if (otherFiles.length)
      jobs.push({ type: otherAttachmentType, files: otherFiles });
    if (ppFiles.length) jobs.push({ type: "ppAttached", files: ppFiles });
    if (!jobs.length) return;

    const aiName = displayName;
    const aiDate = `${form.dateFrom} - ${form.dateTo}`;
    const aiTypeOfActivity =
      selectedType?.name || selectedType?.description || "";
    const aiProvider = form.activityProvider?.trim() || "";
    const aiActivityName = form.activityName?.trim() || "";

    const grandTotal = jobs.reduce(
      (sum, j) => sum + j.files.reduce((s, f) => s + (f.size || 0), 0),
      0,
    );
    let uploadedSoFar = 0;

    setShowUploadModal(true);
    setModalStage("uploading");
    setModalTitle("Uploading Documents");
    setModalMsg("Uploading your documents. Please do not close this page.");
    setModalPct(0);
    setModalDetails("");

    for (let idx = 0; idx < jobs.length; idx++) {
      const { type, files } = jobs[idx];
      const displayIndex = idx + 1;
      const jobsLen = jobs.length;
      const baseUploaded = uploadedSoFar;
      const jobTotal = files.reduce((s, f) => s + (f.size || 0), 0);

      const deleteList = buildDeleteListForType(type);
      const cleanDeleteList = (Array.isArray(deleteList) ? deleteList : [])
        .map((x) => ({ docId: x?.docId }))
        .filter(
          (x) =>
            x.docId !== undefined &&
            x.docId !== null &&
            String(x.docId).trim() !== "",
        );

      const fd = new FormData();
      fd.append("userId", String(targetUserId)); // send to backend
      fd.append("learnId", String(learnId));
      fd.append("doId", "");
      fd.append("attachmentType", type);
      fd.append("type", "update");

      fd.append("name", aiName);
      fd.append("date", aiDate);
      fd.append("activityName", aiActivityName);
      fd.append("typeOfActivity", aiTypeOfActivity);
      fd.append("activityProvider", aiProvider);

      if (cleanDeleteList.length > 0) {
        cleanDeleteList.forEach((item, i) => {
          fd.append(`deleteList[${i}][docId]`, String(item.docId));
        });
      } else {
        fd.append(`deleteList[0][docId]`, "-1");
      }

      for (const f of files) fd.append("files", f);

      const handleProgress = (evt) => {
        const loadedInThisJob = Math.min(evt.loaded || 0, jobTotal);
        const combinedLoaded = baseUploaded + loadedInThisJob;
        const pct = Math.min(
          100,
          Math.round((combinedLoaded * 100) / Math.max(grandTotal, 1)),
        );

        setModalPct(pct);
        setModalDetails(`Uploading ${displayIndex}/${jobsLen}`);
        setModalMsg("Uploading your documents. Please wait…");
      };

      await axios.post(ENDPOINT_UPLOAD, fd, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(targetUserId),
          "x-hid": String(learnId),
          "x-fldrname": "learn",
          "x-filenames": JSON.stringify([]),
        },
        timeout: 180000,
        onUploadProgress: handleProgress,
      });

      uploadedSoFar = baseUploaded + jobTotal;
      setModalPct(
        Math.min(
          100,
          Math.round((uploadedSoFar * 100) / Math.max(grandTotal, 1)),
        ),
      );
    }

    setModalStage("checking");
    setModalTitle("Checking Documents");
    setModalMsg("Upload completed. Now checking your documents with AI.");
    setModalPct(100);
    setModalDetails("Starting…");

    const ok = await pollAiUntilDone(learnId);

    setModalStage("done");
    setModalTitle("Completed");
    setModalMsg(
      ok
        ? "Upload and AI checking completed."
        : "Upload completed. AI checking is still processing (please check later).",
    );
    setModalDetails("");
    setModalPct(100);

    setTimeout(() => {
      closeUploadModal();
    }, 1200);
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }

    const typeObj = findTypeById(form.activityTypeId);
    const catObj = findCatById(form.activityCategoryId);

    const activityType = typeObj?.id || "";
    const activityCategory = catObj?.id || "";
    const activityOther =
      (catObj?.name || "") === "Others" ? form.activityOther.trim() : "";
    const classDeclare = form.trainerActaAclp === "yes" ? 1 : 0;

    const cntCc =
      !isT2AG && isClassroomCourse && form.trainerActaAclp === "no"
        ? proofOtherFiles.length
        : 0;

    const cntVl =
      !isT2AG && !isClassroomCourse && otherAttachmentType === "vlAttached"
        ? proofOtherFiles.length
        : 0;

    const cntSst =
      !isT2AG && !isClassroomCourse && otherAttachmentType === "sstAttached"
        ? proofOtherFiles.length
        : 0;

    const cntPp = proofPpFiles.length;
    const totalAttached = cntCc + cntVl + cntSst + cntPp;

    setSubmitting(true);

    try {
      const payload = {
        learnId: String(editingId),
        userId: String(targetUserId),
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        activityName: form.activityName.trim(),
        activityType: String(activityType),
        activityCategory: String(activityCategory),
        activityOther,
        activityProvider: form.activityProvider?.trim() || "",
        classDeclare,
        isParticipated: Number(Boolean(form.isParticipated)),
        ccNoOfAttach: cntCc,
        vlAttached: cntVl,
        sstAttached: cntSst,
        proofParticipation: cntPp,
        noOfAttached: totalAttached,
        statusName: "Pending",
      };

      const isApprovedEdit =
        String(activityStatus || "")
          .trim()
          .toLowerCase() === "approved";

      const updateUrl = isApprovedEdit
        ? ENDPOINT_UPDATE_APPROVED_LEARN
        : ENDPOINT_UPDATE_LEARN;

      const { data } = await axios.post(updateUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-userid": String(targetUserId),
        },
        timeout: 25000,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Update failed");
      }

      try {
        await uploadBucketsSequentially({
          learnId: editingId,
          otherFiles: showOtherUpload && !isT2AG ? proofOtherFiles : [],
          ppFiles: proofPpFiles,
        });
      } catch (upErr) {
        setFormError(
          upErr?.response?.data?.message ||
            upErr?.message ||
            "Activity updated, but attachments failed to upload. Please try again.",
        );
      }

      setShowSuccess(true);
    } catch (err2) {
      setFormError(
        err2?.response?.data?.message ||
          err2?.message ||
          "Failed to update. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="learn-wrap custom-font"
      style={{ background: PANEL_BG, minHeight: "100vh" }}
    >
      <UploadCheckingModal
        show={showUploadModal}
        onHide={closeUploadModal}
        stage={modalStage}
        pct={modalPct}
        title={modalTitle}
        message={modalMsg}
        details={modalDetails}
      />

      <header
        className="learn-topbar"
        style={{ height: 80, background: "#fff" }}
      >
        <div className="container-xxl h-100 d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <img
              src={LOGO_URL}
              alt="TMIS"
              style={{ height: 50, objectFit: "contain" }}
            />
          </div>
          <div
            className="d-flex align-items-center gap-4"
            style={{ color: "#333" }}
          >
            <Bell size={20} color="#1A3584" />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
            />
            <span>{displayName}</span>
            <PersonCircle
              size={23}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/profile")}
              role="button"
              tabIndex={0}
            />
          </div>
        </div>
      </header>

      <div
        style={{
          background: NAV_BLUE,
          color: "#fff",
          padding: "12px 0",
          boxShadow: "0 2px 0 rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="container-xxl d-flex align-items-center"
          style={{ gap: 12 }}
        >
          <span
            role="button"
            aria-label="Go back"
            tabIndex={0}
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#ffffff" />
          </span>
          <h4 className="m-0 fw-bold">Edit LEARN activity</h4>
        </div>
      </div>

      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} lg={9} xl={8}>
            <Card
              className="shadow-sm"
              style={{
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: PANEL_SURFACE,
              }}
            >
              <Card.Body className="p-4 p-md-5">
                <div className="mb-3">
                  {activityStatus ? (
                    <div className="mb-3">
                      <span style={statusPillStyle(activityStatus)}>
                        {(
                          STATUS_STYLES[activityStatus] || STATUS_STYLES.Default
                        ).icon || null}
                        {activityStatus}
                      </span>
                    </div>
                  ) : null}

                  <Alert
                    variant="primary"
                    className="d-flex justify-content-between align-items-start"
                  >
                    <div>
                      <div className="fw-bold mb-1">Editing LEARN activity</div>
                      <div style={{ fontSize: 13 }}>
                        Update LEARN submission and upload new documents if
                        needed.
                      </div>
                      {(loadingDetail || loadingCycle) && (
                        <div
                          className="mt-2 text-muted"
                          style={{ fontSize: 12 }}
                        >
                          <Spinner animation="border" size="sm" /> Loading
                          details…
                        </div>
                      )}
                    </div>
                  </Alert>

                  {editRemarks && (
                    <Alert variant="warning">
                      <div className="fw-bold mb-1">Remarks from reviewer</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {editRemarks}
                      </div>
                    </Alert>
                  )}
                </div>

                {formError && (
                  <Alert variant="danger" className="mb-3">
                    {formError}
                  </Alert>
                )}

                {selectsError && (
                  <Alert variant="warning" className="mb-3">
                    {selectsError}
                  </Alert>
                )}

                <Form onSubmit={onSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="learnFrom">
                        <Form.Label className="mb-1 fw-bold">
                          Activity Date From
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateFrom}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField("dateFrom", v);
                            if (form.dateTo && v && form.dateTo < v) {
                              setField("dateTo", "");
                            }
                          }}
                          min={cycle.start || FRONTEND_MIN_START}
                          max={cycle.end || undefined}
                          required
                          disabled={loadingDetail}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="learnTo">
                        <Form.Label className="mb-1 fw-bold">
                          Activity Date To
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={form.dateTo}
                          onChange={(e) => setField("dateTo", e.target.value)}
                          min={
                            form.dateFrom || cycle.start || FRONTEND_MIN_START
                          }
                          max={cycle.end || undefined}
                          required
                          disabled={loadingDetail}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="learnName">
                    <Form.Label className="mb-1 fw-bold">
                      Name of Activity
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={form.activityName}
                      onChange={(e) => setField("activityName", e.target.value)}
                      style={{ background: "#fff" }}
                      required
                      disabled={loadingDetail}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="learnType">
                    <Form.Label className="mb-1 fw-bold">
                      Type of Activity
                    </Form.Label>
                    <Form.Select
                      value={form.activityTypeId}
                      onChange={(e) =>
                        setField("activityTypeId", e.target.value)
                      }
                      required
                      disabled={
                        loadingSelects || !!selectsError || loadingDetail
                      }
                    >
                      <option value="">
                        {loadingSelects ? "Loading..." : "-- Select Type --"}
                      </option>
                      {selects.activityTypes.map((t) => (
                        <option
                          key={t.id}
                          value={t.id}
                          title={t.description || ""}
                        >
                          {t.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {isClassroomCourse && !isT2AG && (
                    <Form.Group className="mb-3" controlId="trainerActaAclp">
                      <Form.Label className="mb-1 fw-bold">
                        Does the trainer have an ACTA/ACLP certification?
                      </Form.Label>
                      <div className="d-flex gap-4">
                        <Form.Check
                          type="radio"
                          id="acta-yes"
                          name="trainerActaAclp"
                          label="Yes"
                          value="yes"
                          checked={form.trainerActaAclp === "yes"}
                          onChange={() => setField("trainerActaAclp", "yes")}
                          required
                          disabled={loadingDetail}
                        />
                        <Form.Check
                          type="radio"
                          id="acta-no"
                          name="trainerActaAclp"
                          label="No"
                          value="no"
                          checked={form.trainerActaAclp === "no"}
                          onChange={() => setField("trainerActaAclp", "no")}
                          required
                          disabled={loadingDetail}
                        />
                      </div>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3" controlId="learnCategory">
                    <Form.Label className="mb-1 fw-bold">
                      LEARN Category
                    </Form.Label>
                    <Form.Select
                      value={form.activityCategoryId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setField("activityCategoryId", id);
                        const found = selects.learnCategories.find(
                          (x) => String(x.id) === String(id),
                        );
                        if ((found?.name || "") !== "Others") {
                          setField("activityOther", "");
                        }
                      }}
                      required
                      disabled={
                        loadingSelects || !!selectsError || loadingDetail
                      }
                    >
                      <option value="">
                        {loadingSelects
                          ? "Loading..."
                          : "-- Select Category --"}
                      </option>
                      {selects.learnCategories.map((c) => (
                        <option
                          key={c.id}
                          value={c.id}
                          title={c.description || ""}
                        >
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {(() => {
                    const found = findCatById(form.activityCategoryId);
                    if ((found?.name || "") !== "Others") return null;

                    return (
                      <Form.Group className="mb-3" controlId="learnOther">
                        <Form.Label className="mb-1 fw-bold">
                          Please specify
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Describe the LEARN category"
                          value={form.activityOther}
                          onChange={(e) =>
                            setField("activityOther", e.target.value)
                          }
                          required
                          disabled={loadingDetail}
                        />
                      </Form.Group>
                    );
                  })()}

                  <Form.Group className="mb-3" controlId="activityProvider">
                    <Form.Label className="mb-1 fw-bold">
                      Activity Provider
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Please enter the name of the organisation"
                      value={form.activityProvider}
                      onChange={(e) =>
                        setField("activityProvider", e.target.value)
                      }
                      disabled={loadingDetail}
                    />
                  </Form.Group>

                  {showOtherUpload && (
                    <Form.Group className="mb-4" controlId="proofOtherActivity">
                      <div className="d-flex align-items-start justify-content-between">
                        <Form.Label className="mb-2 fw-bold">
                          Supporting Document (Other Activities / Classroom
                          Declaration)
                          <div
                            style={{ fontSize: "12px", color: "#1F2937" }}
                            className="fw-normal"
                          >
                            (Maximum 3 files allowed, ≤ 15 MB)
                          </div>
                          {proofOtherFiles.length > 0 && (
                            <div
                              style={{ fontSize: "12px" }}
                              className="fw-normal"
                            >
                              {proofOtherFiles.length} / 3 selected
                            </div>
                          )}
                        </Form.Label>

                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => proofOtherRef.current?.click()}
                          style={{ borderRadius: 18, lineHeight: 1.1 }}
                          type="button"
                        >
                          Select Files
                        </Button>
                      </div>

                      {(existingDocs.cc.length > 0 ||
                        existingDocs.vl.length > 0 ||
                        existingDocs.sst.length > 0) && (
                        <div
                          className="mt-2"
                          style={{
                            fontSize: 12,
                            background: "#EFF6FF",
                            borderRadius: 8,
                            padding: "8px 10px",
                          }}
                        >
                          <div className="fw-bold mb-1">
                            Existing supporting documents
                          </div>
                          <ul className="mb-0" style={{ paddingLeft: 18 }}>
                            {existingDocs.cc.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Classroom Courses – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                            {existingDocs.vl.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Virtual – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                            {existingDocs.sst.map((d) => (
                              <li key={d.doc_id ?? d.id}>
                                Site-spec – {d.file_name}{" "}
                                <span className="text-muted">
                                  ({formatDocDate(d.uploaded_date)})
                                </span>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-1 text-muted">
                            Uploading new files will replace the existing files
                            for that same document group.
                          </div>
                        </div>
                      )}

                      <input
                        ref={proofOtherRef}
                        type="file"
                        name="files"
                        accept={allowed}
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const { next, skippedNames } = addFiles(
                            proofOtherFiles,
                            e.target.files,
                            3,
                          );
                          setProofOtherFiles(next);
                          if (skippedNames.length) {
                            setFormError(`Skipped: ${skippedNames.join(", ")}`);
                          }
                          e.target.value = "";
                        }}
                      />

                      <div
                        className="mt-1"
                        style={{
                          background: "#fff",
                          border: "2px dashed #cfd6e0",
                          color: "#1F2937",
                          borderRadius: 12,
                          padding: "16px 14px",
                          minHeight: 56,
                        }}
                      >
                        {proofOtherFiles.length === 0 ? (
                          <div>No new file selected</div>
                        ) : (
                          <ul className="mb-0" style={{ paddingLeft: 16 }}>
                            {proofOtherFiles.map((f, idx) => (
                              <li
                                key={`${f.name}-${f.size}-${idx}`}
                                className="d-flex justify-content-between align-items-center"
                              >
                                <span
                                  className="me-2"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {f.name} ({Math.round(f.size / 1024)} KB)
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() =>
                                    setProofOtherFiles((list) =>
                                      list.filter((_, i) => i !== idx),
                                    )
                                  }
                                  style={{ borderRadius: 18 }}
                                  type="button"
                                >
                                  Remove
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </Form.Group>
                  )}

                  <Form.Group className="mb-4" controlId="proofParticipation">
                    <div className="d-flex align-items-start justify-content-between">
                      <Form.Label className="mb-2 fw-bold">
                        Proof of Participation
                        <div
                          style={{ fontSize: "12px", color: "#1F2937" }}
                          className="fw-normal"
                        >
                          (Maximum 3 files allowed, ≤ 15 MB)
                        </div>
                        {proofPpFiles.length > 0 && (
                          <div
                            style={{ fontSize: "12px" }}
                            className="fw-normal"
                          >
                            {proofPpFiles.length} / 3 selected
                          </div>
                        )}
                      </Form.Label>

                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => proofPpRef.current?.click()}
                        style={{ borderRadius: 18, lineHeight: 1.1 }}
                        type="button"
                      >
                        Select Files
                      </Button>
                    </div>

                    {existingDocs.pp.length > 0 && (
                      <div
                        className="mt-2"
                        style={{
                          fontSize: 12,
                          background: "#EFF6FF",
                          borderRadius: 8,
                          padding: "8px 10px",
                        }}
                      >
                        <div className="fw-bold mb-1">
                          Existing proof of participation
                        </div>
                        <ul className="mb-0" style={{ paddingLeft: 18 }}>
                          {existingDocs.pp.map((d) => (
                            <li key={d.doc_id ?? d.id}>
                              {d.file_name}{" "}
                              <span className="text-muted">
                                ({formatDocDate(d.uploaded_date)})
                              </span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-1 text-muted">
                          Uploading new files will replace the existing proof of
                          participation files.
                        </div>
                      </div>
                    )}

                    <input
                      ref={proofPpRef}
                      type="file"
                      name="files"
                      accept={allowed}
                      multiple
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const { next, skippedNames } = addFiles(
                          proofPpFiles,
                          e.target.files,
                          3,
                        );
                        setProofPpFiles(next);
                        if (skippedNames.length) {
                          setFormError(`Skipped: ${skippedNames.join(", ")}`);
                        }
                        e.target.value = "";
                      }}
                    />

                    <div
                      className="mt-1"
                      style={{
                        background: "#fff",
                        border: "2px dashed #cfd6e0",
                        color: "#1F2937",
                        borderRadius: 12,
                        padding: "16px 14px",
                        minHeight: 56,
                      }}
                    >
                      {proofPpFiles.length === 0 ? (
                        <div>No new file selected</div>
                      ) : (
                        <ul className="mb-0" style={{ paddingLeft: 16 }}>
                          {proofPpFiles.map((f, idx) => (
                            <li
                              key={`${f.name}-${f.size}-${idx}`}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span
                                className="me-2"
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {f.name} ({Math.round(f.size / 1024)} KB)
                              </span>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() =>
                                  setProofPpFiles((list) =>
                                    list.filter((_, i) => i !== idx),
                                  )
                                }
                                style={{ borderRadius: 18 }}
                                type="button"
                              >
                                Remove
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="isParticipated">
                    <Form.Check
                      type="checkbox"
                      label="I declare that the information and documents submitted for this Learn activity are true, accurate and complete to the best of my knowledge."
                      checked={form.isParticipated}
                      onChange={(e) =>
                        setField("isParticipated", e.target.checked)
                      }
                      className="small"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleBack}
                      style={{ minWidth: 120, borderRadius: 20 }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      disabled={
                        submitting ||
                        !form.isParticipated ||
                        loadingSelects ||
                        loadingDetail
                      }
                      style={{
                        background: BRAND,
                        border: "none",
                        minWidth: 140,
                        borderRadius: 20,
                      }}
                    >
                      {submitting ? "Updating..." : "Update"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showSuccess}
        onHide={() => setShowSuccess(false)}
        centered
        backdrop="static"
        keyboard
      >
        <Modal.Body className="text-center p-4 custom-font">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "rgba(16,185,129,0.12)",
            }}
          >
            <CheckCircleFill size={44} color="#10B981" />
          </div>
          <h5 className="fw-bold mb-2">Update Successful</h5>
          <div className="text-muted mb-3" style={{ lineHeight: 1.6 }}>
            Your LEARN activity was updated successfully.
          </div>
          <div className="d-flex justify-content-center gap-2">
            <Button
              onClick={() => {
                setShowSuccess(false);

                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                }
              }}
              style={{ background: BRAND, border: "none", minWidth: 120 }}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
